import React, { useState, useEffect, useMemo } from 'react';
import './App.css';
import {
  CITIES, CINSIYET, MEDENI_HAL, EGITIM, EHLIYET,
  isValidTCKN, toDateInput,
} from './formOptions';

const API = 'https://api.idekod.com.tr';

/* Formun tuttuğu alanlar -> personel tablosundaki kolon adları */
const EMPTY_FORM = {
  RNAME: '', USERN: '', PASSWORD: '', telno: '', eposta: '',
  TCKN: '', CINSIYET: '', MEDENI_HAL: '', egitimd: '', EHLIYET_SINIFI: '',
  SEHIR: '', DOGUM_YERI: '', ANNE_ADI: '', BABA_ADI: '', ADRES: '',
  dogumt: '', girist: '', STATE: '', COMPANY: '', role: '',
};

const fromRecord = (p) => ({
  RNAME: p.RNAME ?? '',
  USERN: p.USERN ?? '',
  PASSWORD: p.PASSWORD ?? '',
  telno: p.telno ?? '',
  eposta: p.eposta ?? '',
  TCKN: p.TCKN ?? '',
  CINSIYET: p.CINSIYET ?? '',
  MEDENI_HAL: p.MEDENI_HAL ?? '',
  egitimd: p.egitimd ?? '',
  EHLIYET_SINIFI: p.EHLIYET_SINIFI ?? '',
  SEHIR: p.SEHIR ?? '',
  DOGUM_YERI: p.DOGUM_YERI ?? '',
  ANNE_ADI: p.ANNE_ADI ?? '',
  BABA_ADI: p.BABA_ADI ?? '',
  ADRES: p.ADRES ?? '',
  dogumt: p.dogumt_str ?? toDateInput(p.dogumt),
  girist: p.girist_str ?? toDateInput(p.girist),
  STATE: p.STATE ?? '',
  COMPANY: p.COMPANY ?? '',
  role: p.role ?? '',
});

/* Kayıtlı değer listede yoksa seçenek olarak ekler; kaydederken veri kaybını önler */
const withCurrent = (options, current) =>
  current && !options.includes(current) ? [current, ...options] : options;

const Personel = () => {
  const [personelList, setPersonelList] = useState([]);
  const [selectedPK, setSelectedPK] = useState('');
  const [form, setForm] = useState(EMPTY_FORM);
  const [photo, setPhoto] = useState(null);      // yeni seçilen foto (base64)
  const [currentPhoto, setCurrentPhoto] = useState('');
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success' | 'error', text }

  const loadList = () =>
    fetch(`${API}/api/personeledit`)
      .then((response) => response.json())
      .then((data) => setPersonelList(Array.isArray(data) ? data : []))
      .catch((error) => console.error('Personel bilgileri alınırken hata oluştu:', error));

  useEffect(() => { loadList(); }, []);

  const selectedPersonel = useMemo(
    () => personelList.find((p) => String(p.PK) === String(selectedPK)) || null,
    [personelList, selectedPK]
  );

  const handleSelectChange = (e) => {
    const pk = e.target.value;
    setSelectedPK(pk);
    setFeedback(null);
    setPhoto(null);

    const selected = personelList.find((p) => String(p.PK) === String(pk));
    if (selected) {
      setForm(fromRecord(selected));
      setCurrentPhoto(selected.FOTO_URL || '');
    } else {
      setForm(EMPTY_FORM);
      setCurrentPhoto('');
    }
  };

  const setField = (name) => (e) => setForm((f) => ({ ...f, [name]: e.target.value }));

  const tcknValid = !form.TCKN || isValidTCKN(form.TCKN);

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => setPhoto(reader.result);
    reader.readAsDataURL(file);
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!selectedPersonel) return;
    if (!tcknValid) {
      setFeedback({ type: 'error', text: 'TCKN geçersiz. Lütfen kontrol edin.' });
      return;
    }

    setSaving(true);
    setFeedback(null);

    const payload = { ...form, PK: selectedPersonel.PK };
    if (photo) payload.FOTO_URL = photo;   // foto seçilmediyse mevcut değere dokunulmaz

    try {
      const response = await fetch(
        `${API}/api/personel/telno/${encodeURIComponent(selectedPersonel.telno)}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        }
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(data?.error || 'Güncelleme başarısız oldu.');
      }

      await loadList();
      if (photo) { setCurrentPhoto(photo); setPhoto(null); }
      setFeedback({ type: 'success', text: data?.message || 'Personel bilgileri güncellendi.' });
    } catch (error) {
      console.error('Personel bilgileri güncellenirken hata oluştu:', error);
      setFeedback({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const selectField = (label, name, options) => (
    <div className="form-field">
      <label className="form-label">{label}</label>
      <select value={form[name]} onChange={setField(name)} className="form-control">
        <option value="">Seçiniz</option>
        {withCurrent(options, form[name]).map((opt) => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>
    </div>
  );

  const textField = (label, name, type = 'text') => (
    <div className="form-field">
      <label className="form-label">{label}</label>
      <input type={type} value={form[name]} onChange={setField(name)} className="form-control" />
    </div>
  );

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Personel Düzenle</h1>
          <p className="page__subtitle">Kayıtlı bir personelin tüm bilgilerini güncelleyin</p>
        </div>
      </div>

      <div className="card">
        <div className="card__header">
          <h2 className="card__title">Personel Seçimi</h2>
        </div>

        <div className="card__body">
          <div className="form-field">
            <label className="form-label">Düzenlenecek kullanıcı</label>
            <select className="form-control" onChange={handleSelectChange} value={selectedPK}>
              <option value="">Kullanıcı Seçin</option>
              {personelList.map((personel) => (
                <option key={personel.PK} value={String(personel.PK)}>
                  {personel.RNAME} {personel.telno ? `— ${personel.telno}` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {selectedPersonel && (
        <form onSubmit={handleUpdate}>
          <div className="card">
            <div className="card__header">
              <h2 className="card__title">Kimlik ve Hesap Bilgileri</h2>
            </div>

            <div className="card__body form">
              <div className="form-field">
                <label className="form-label">Fotoğraf</label>
                {(photo || currentPhoto) && (
                  <img
                    src={photo || currentPhoto}
                    alt="Personel fotoğrafı"
                    className="photo-preview u-mb-2"
                  />
                )}
                <input type="file" accept="image/*" onChange={handlePhotoChange} className="form-control" />
                <span className="form-helper">
                  Yeni dosya seçmezseniz mevcut fotoğraf korunur.
                </span>
              </div>

              <div className="form-grid">
                <div className="form-field">
                  <label className="form-label">T.C. Kimlik Numarası</label>
                  <input
                    type="text"
                    value={form.TCKN}
                    onChange={setField('TCKN')}
                    className={`form-control ${!tcknValid ? 'form-control--invalid' : ''}`}
                  />
                  {!tcknValid && <span className="form-error">Geçersiz TCKN</span>}
                </div>

                {textField('Kullanıcı Ad Soyad', 'RNAME')}
                {textField('Kullanıcı ID', 'USERN')}
                {textField('Şifre', 'PASSWORD', 'password')}
                {textField('Telefon', 'telno')}
                {textField('E-posta', 'eposta', 'email')}
              </div>
            </div>
          </div>

          <div className="card u-mt-4">
            <div className="card__header">
              <h2 className="card__title">Kişisel Bilgiler</h2>
            </div>

            <div className="card__body form">
              <div className="form-grid">
                {selectField('Cinsiyet', 'CINSIYET', CINSIYET)}
                {selectField('Medeni Hal', 'MEDENI_HAL', MEDENI_HAL)}
                {selectField('Eğitim Durumu', 'egitimd', EGITIM)}
                {selectField('Ehliyet', 'EHLIYET_SINIFI', EHLIYET)}
                {selectField('Şehir', 'SEHIR', CITIES)}
                {selectField('Doğum Yeri', 'DOGUM_YERI', CITIES)}
                {textField('Anne Ad Soyad', 'ANNE_ADI')}
                {textField('Baba Ad Soyad', 'BABA_ADI')}
                {textField('Doğum Tarihi', 'dogumt', 'date')}
                {textField('İşe Başlangıç Tarihi', 'girist', 'date')}

                <div className="form-field form-field--full">
                  <label className="form-label">Adres</label>
                  <input type="text" value={form.ADRES} onChange={setField('ADRES')} className="form-control" />
                </div>
              </div>
            </div>
          </div>

          <div className="card u-mt-4">
            <div className="card__header">
              <h2 className="card__title">Kurum ve Yetki</h2>
            </div>

            <div className="card__body form">
              <div className="form-grid">
                {textField('Şirket Kısa Kod', 'COMPANY')}
                <div className="form-field">
                  <label className="form-label">Durum (0-Pasif / 1-Aktif / 3-Bekleyen)</label>
                  <input type="text" value={form.STATE} onChange={setField('STATE')} className="form-control" />
                </div>
                <div className="form-field">
                  <label className="form-label">Rol (0-Kullanıcı / 1-Admin / 2-Ekip Şefi)</label>
                  <input type="text" value={form.role} onChange={setField('role')} className="form-control" />
                </div>
              </div>

              {feedback && (
                <div className={`info-banner ${feedback.type === 'error' ? 'info-banner--error' : 'info-banner--success'}`}>
                  {feedback.text}
                </div>
              )}

              <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={saving}>
                {saving ? 'Kaydediliyor…' : 'Güncelle'}
              </button>
            </div>
          </div>
        </form>
      )}
    </div>
  );
};

export default Personel;
